import styled, { keyframes } from 'styled-components';
import { Table, Input, Select, DatePicker, Typography, Button, Modal, Tabs } from 'antd';
import { COLORS } from '../../constants/colors';

const { Title } = Typography;

// Animation keyframes
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const fadeInDown = keyframes`
  from {
    opacity: 0;
    transform: translateY(-15px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

// slideInLeft animation - kept for potential future use
// eslint-disable-next-line no-unused-vars
const slideInLeft = keyframes`
  from {
    opacity: 0;
    transform: translateX(-20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const slideInRight = keyframes`
  from {
    opacity: 0;
    transform: translateX(20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const scaleIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

// Tab content wrapper with smooth entrance animations
export const TabContentWrapper = styled.div`
  opacity: 0;
  animation: ${fadeIn} 0.4s ease-out forwards;
  animation-delay: 0.05s;
`;

// Animated section wrapper for staggered animations
export const AnimatedSection = styled.div`
  opacity: 0;
  animation: ${fadeInUp} 0.5s ease-out forwards;
  animation-delay: ${props => props.$delay || '0s'};
`;

// Filters row with slide-in animation
export const AnimatedFiltersRow = styled.div`
  opacity: 0;
  animation: ${fadeInDown} 0.4s ease-out forwards;
  animation-delay: 0.1s;
`;

// Table/Content area with fade-up animation  
export const AnimatedTableSection = styled.div`
  opacity: 0;
  animation: ${fadeInUp} 0.5s ease-out forwards;
  animation-delay: 0.2s;
`;

// Calendar specific animation
export const AnimatedCalendarSection = styled.div`
  opacity: 0;
  animation: ${scaleIn} 0.5s ease-out forwards;
  animation-delay: 0.15s;
`;

// Side panel animation (slides from right)
export const AnimatedSidePanel = styled.div`
  opacity: 0;
  animation: ${slideInRight} 0.5s ease-out forwards;
  animation-delay: ${props => props.$delay || '0.25s'};
`;

// Card grid item with stagger effect
export const AnimatedCard = styled.div`
  opacity: 0;
  animation: ${fadeInUp} 0.4s ease-out forwards;
  animation-delay: ${props => props.$delay || '0s'};
  
  &:hover {
    transform: translateY(-2px);
    transition: transform 0.2s ease;
  }
`;

// Form section animation
export const AnimatedFormSection = styled.div`
  opacity: 0;
  animation: ${fadeIn} 0.5s ease-out forwards;
  animation-delay: ${props => props.$delay || '0.1s'};
`;

// Preview panel animation
export const AnimatedPreviewPanel = styled.div`
  opacity: 0;
  animation: ${slideInRight} 0.5s ease-out forwards;
  animation-delay: 0.3s;
`;

// Row items with stagger (for grids/lists)
export const StaggeredRow = styled.div`
  display: contents;
  
  & > * {
    opacity: 0;
    animation: ${fadeInUp} 0.4s ease-out forwards;
  }
  
  & > *:nth-child(1) { animation-delay: 0.1s; }
  & > *:nth-child(2) { animation-delay: 0.15s; }
  & > *:nth-child(3) { animation-delay: 0.2s; }
  & > *:nth-child(4) { animation-delay: 0.25s; }
  & > *:nth-child(5) { animation-delay: 0.3s; }
  & > *:nth-child(6) { animation-delay: 0.35s; }
`;

// Main containers
export const PageContainer = styled.div`
  /* Animation Keyframes */
  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(30px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  @keyframes fadeInDown {
    from {
      opacity: 0;
      transform: translateY(-20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  /* Header Animations */
  .header-animate-in {
    animation: fadeInDown 0.6s ease-out forwards;
    opacity: 0;
  }
  
  .header-animate-out {
    opacity: 0;
    transform: translateY(-20px);
  }
`;

export const HeaderSection = styled.div`
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 2px solid ${COLORS.border};
`;

export const DashboardTitle = styled.h1`
  margin: 0;
  color: ${COLORS.textDark};
  font-size: 24px;
  font-weight: 500;
  line-height: 1.3;
  letter-spacing: -0.3px;
  display: flex;
  align-items: center;
  gap: 12px;
`;

export const StatsCards = styled.div`
  margin-bottom: 24px;

  /* On mobile the stats live in the status chips + filter sheet, so the large
     stats card is hidden to bring the tabs to the top (matching the app UI). */
  @media (max-width: 767px) {
    display: none;
  }
`;

export const StatsCard = styled.div`
  .ant-card {
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
    border: 1px solid #f0f0f0;
    transition: all 0.3s ease;
    
    &:hover {
      box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
      transform: translateY(-2px);
    }
  }
`;

export const StatNumber = styled.div`
  font-size: 32px;
  font-weight: bold;
  color: ${COLORS.$primaryBlue};
  margin-bottom: 8px;
  line-height: 1;
`;

export const StatLabel = styled.div`
  font-size: 14px;
  color: #666;
  font-weight: 500;
`;

export const MainContent = styled.div`
  .ant-card {
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
    border: 1px solid #f0f0f0;
  }
  
  .ant-tabs-nav {
    margin-bottom: 24px;
  }
  
  .ant-tabs-tab {
    font-weight: 500;
    color: ${COLORS.textMedium};
    
    &.ant-tabs-tab-active {
      color: ${COLORS.$primaryBlue};
    }
  }
  
  .ant-btn-primary {
    background: ${COLORS.$primaryBlue};
    border-color: ${COLORS.$primaryBlue};
    
    &:hover {
      background: ${COLORS.$primaryBlueHover};
      border-color: ${COLORS.$primaryBlueHover};
    }
    
    &:focus, &:active {
      background: ${COLORS.$primaryBlueDark};
      border-color: ${COLORS.$primaryBlueDark};
    }
  }
`;

// Event Type Manager Styles
export const EventTypeGrid = styled.div`
  .ant-row {
    margin-bottom: 16px;
  }
`;

export const EventTypeCard = styled.div`
  .ant-card {
    border-radius: 12px;
    border: 1.5px solid transparent;
    transition: all 0.3s ease;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
    
    &:hover {
      border-color: ${COLORS.$primaryBlue};
      transform: translateY(-4px);
      box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
      cursor: pointer;
    }
  }
`;

export const EventTypeHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;

export const EventTypeIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  margin-bottom: 12px;
`;

export const EventTypeTitle = styled(Title)`
  margin: 0 0 8px 0 !important;
  font-size: 18px !important;
`;

export const EventTypeDescription = styled.p`
  color: #666;
  margin: 0 0 16px 0;
  font-size: 14px;
  line-height: 1.6;
  min-height: 48px;
  display: flex;
  align-items: center;
`;

export const EventTypeMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 16px;
  
  .meta-item {
    display: flex;
    align-items: center;
    gap: 4px;
    color: #666;
    font-size: 13px;
  }
`;

export const ActionButton = styled(Button)`
  height: 40px;
  font-size: 14px;
  font-weight: 600;
  padding: 0 24px;
  border: none;
  box-shadow: none !important;
  background: ${COLORS.$primaryBlue};
  color: white;
  border-radius: 8px;
  
  &:hover,
  &:focus,
  &:active {
    background: ${COLORS.$primaryBlueHover};
    color: white;
    box-shadow: none !important;
  }
  
  &.ant-btn-dangerous,
  &.ant-btn-dangerous:hover,
  &.ant-btn-dangerous:focus,
  &.ant-btn-dangerous:active {
    background: ${COLORS.$primaryBlue};
    border-color: ${COLORS.$primaryBlue};
    color: white;
    box-shadow: none !important;
  }
`;

// Calendar Manager Styles
export const CalendarSection = styled.div`
  .ant-calendar-picker {
    width: 100%;
  }
`;

export const TimeSlotList = styled.div`
  max-height: 400px;
  overflow-y: auto;
  padding: 16px;
  border: 1px solid #f0f0f0;
  border-radius: 6px;
  background: #fafafa;
`;

export const TimeSlotItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px;
  background: white;
  border-radius: 6px;
  margin-bottom: 8px;
  border: 1px solid #f0f0f0;
  
  &:hover {
    border-color: ${COLORS.primary};
  }
`;

// Booking Manager Styles
export const BookingFilters = styled.div`
  margin-bottom: 8px;
  width: 100%;
  
  .ant-row {
    display: flex !important;
    flex-wrap: nowrap !important;
    align-items: center !important;
    gap: 8px;
  }
  
  .ant-col {
    padding: 0 !important;
  }
  
  .search-input {
    flex: 1 !important;
    min-width: 0;
    
    .ant-input {
      height: 40px !important;
      border-radius: 6px;
      border: 1px solid #d9d9d9;
      transition: all 0.3s ease;
      display: flex;
      align-items: center;
      
      &:hover {
        border-color: ${COLORS.$primaryBlue};
      }
      
      &:focus {
        border-color: ${COLORS.$primaryBlue};
        box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.1);
      }
    }
    
    /* Fix for Input with prefix icon */
    .ant-input-affix-wrapper {
      height: 40px !important;
      border-radius: 6px;
      border: 1px solid #d9d9d9;
      transition: all 0.3s ease;
      display: flex;
      align-items: center;
      
      &:hover {
        border-color: ${COLORS.$primaryBlue};
      }
      
      &:focus-within {
        border-color: ${COLORS.$primaryBlue};
        box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.1);
      }
      
      .ant-input {
        border: none !important;
        box-shadow: none !important;
        height: auto !important;
        padding: 0;
      }
      
      .ant-input-prefix {
        margin-right: 8px;
        color: #999;
      }
    }
  }
  
  .filter-controls {
    flex: 0 0 auto !important;
    display: flex;
    align-items: center;
    
    .ant-select {
      width: 100% !important;
      min-width: 120px;
      height: 40px;
      
      .ant-select-selector {
        height: 40px !important;
        border-radius: 8px !important;
        border: 1.5px solid #e8e8e8 !important;
        transition: all 0.3s ease !important;
        display: flex !important;
        align-items: center !important;
        
        &:hover {
          border-color: ${COLORS.$primaryBlue} !important;
        }
      }
      
      &.ant-select-focused .ant-select-selector {
        border-color: ${COLORS.$primaryBlue} !important;
        box-shadow: 0 0 0 3px rgba(24, 144, 255, 0.15) !important;
      }
    }
    
    .ant-btn {
      height: 40px !important;
      min-width: 100px;
      border-radius: 6px;
      font-weight: 600;
      font-size: 13px;
      box-shadow: none !important;
      transition: all 0.3s ease;
      display: flex;
      align-items: center;
      justify-content: center;
      
      &:hover {
        border-color: ${COLORS.$primaryBlue};
        color: ${COLORS.$primaryBlue};
        background: rgba(24, 144, 255, 0.05);
      }
    }
  }
  
  /* Ensure all input search components have consistent height */
  .ant-input-search {
    .ant-input-group {
      display: flex;
      align-items: center;
      
      .ant-input-group-addon {
        height: 40px;
        display: flex;
        align-items: center;
        
        .ant-btn {
          height: 40px !important;
          display: flex;
          align-items: center;
          justify-content: center;
        }
      }
    }
    
    .ant-input-search-button {
      height: 40px !important;
      display: flex;
      align-items: center;
      justify-content: center;
    }
  }
  
  .ant-input-search .ant-input-group .ant-input {
    height: 40px !important;
  }
  
  .ant-input-search .ant-input-group .ant-btn {
    height: 40px !important;
  }
  
  /* Mobile responsive layout */
  @media (max-width: 767px) {
    margin-bottom: 12px;

    .ant-row {
      flex-wrap: wrap !important;
      gap: 8px;
    }

    .search-input {
      width: 100% !important;
      flex: 0 0 100% !important;
      max-width: 100% !important;

      .ant-input-affix-wrapper { height: 44px !important; }
    }

    .date-range-filter {
      width: 100% !important;
      flex: 0 0 100% !important;
      max-width: 100% !important;

      .ant-picker {
        width: 100% !important;
        height: 44px !important;
        border-radius: 8px;
      }
    }

    .filter-controls {
      width: 100% !important;
      flex: 0 0 100% !important;
      max-width: 100% !important;
      display: flex;
      gap: 8px;

      .ant-btn {
        flex: 1 1 0;
        min-width: 0 !important;
        height: 44px !important;
        margin: 0 !important;
        padding: 0 8px;
        white-space: nowrap;
      }

      .ant-select {
        min-width: 100%;
        width: 100% !important;
      }
    }
  }
`;

export const BookingTable = styled(Table)`
  /* Animation support */
  animation-fill-mode: both;
  background: white;
  border-radius: 16px;
  border: 1px solid #f0f0f0;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  
  .ant-table {
    border-radius: 0;
    overflow: hidden;
    background: white;
    border: none;
  }
  
  .ant-table-container {
    overflow-x: auto;
  }
  
  .ant-table-thead > tr > th {
    background: white !important;
    color: ${COLORS.textDark} !important;
    border-bottom: 3px solid ${COLORS.$primaryBlue} !important;
    font-weight: 600 !important;
    font-size: 13px !important;
    height: 52px !important;
    padding: 14px 20px !important;
    text-transform: uppercase !important;
    letter-spacing: 0.5px !important;
    position: relative;
    
    &:first-child {
      border-top-left-radius: 16px;
    }
    
    &:last-child {
      border-top-right-radius: 16px;
    }
  }
  
  /* Override sorted column header background */
  .ant-table-thead > tr > th.ant-table-column-sort {
    background: white !important;
  }
  
  .ant-table-tbody > tr {
    transition: all 0.2s ease;
    border-bottom: 1px solid #f5f5f5;
    
    &:hover > td {
      background: #fafbff !important;
      transform: translateY(-1px);
    }
    
    &:last-child {
      border-bottom: none;
      
      td {
        border-bottom: none;
        border-bottom-left-radius: 0;
        border-bottom-right-radius: 0;
      }
    }
    
    /* Alternating row colors for better readability */
    &:nth-child(even) {
      background: #fafafa;
      
      &:hover > td {
        background: #f0f7ff !important;
      }
    }
  }
  
  .ant-table-tbody > tr > td {
    border-bottom: none;
    padding: 18px 20px;
    font-size: 14px;
    line-height: 1.5;
    vertical-align: middle;
    text-align: left;
    transition: all 0.2s ease;
    background: inherit !important;
  }
  
  /* Override sorted column cell background - inherit from row */
  .ant-table-tbody > tr > td.ant-table-column-sort {
    background: inherit !important;
  }
  
  /* Pagination styling - matching Patient Management with white background */
  .ant-pagination {
    margin: 0 !important;
    padding: 12px 16px !important;
    background: white !important;
    border-top: 1px solid ${COLORS.border};
    border-bottom-left-radius: 16px !important;
    border-bottom-right-radius: 16px !important;
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
    
    .ant-pagination-total-text {
      font-size: 13px;
      color: ${COLORS.textMedium};
      font-weight: 500;
      flex: 1;
    }
    
    .ant-pagination-options {
      display: flex;
      align-items: center;
      gap: 12px;
      height: 32px;
      
      .ant-pagination-options-size-changer {
        height: 32px !important;
        
        .ant-select-selector {
          border-radius: 16px !important;
          border: 1.5px solid ${COLORS.border} !important;
          height: 32px !important;
          min-height: 32px !important;
          min-width: 120px !important;
          transition: all 0.3s ease !important;
          display: flex !important;
          align-items: center !important;
          padding: 0 32px 0 16px !important;
          box-sizing: border-box !important;
          
          &:hover {
            border-color: ${COLORS.$primaryBlue} !important;
            box-shadow: 0 2px 8px rgba(24, 144, 255, 0.15) !important;
          }
        }
        
        &.ant-select-focused .ant-select-selector {
          border-color: ${COLORS.$primaryBlue} !important;
          box-shadow: 0 0 0 3px rgba(24, 144, 255, 0.15) !important;
        }
        
        /* Dropdown menu styling */
        .ant-select-dropdown {
          border-radius: 12px !important;
          box-shadow: 0 6px 24px rgba(0, 0, 0, 0.15) !important;
          border: 1px solid ${COLORS.border} !important;
          
          .ant-select-item {
            padding: 12px 16px !important;
            font-weight: 500 !important;
            transition: all 0.2s ease !important;
            min-height: 32px !important;
            font-size: 14px !important;
            line-height: 1.4 !important;
            display: flex !important;
            align-items: center !important;
            
            &:hover {
              background: rgba(24, 144, 255, 0.1) !important;
              color: ${COLORS.$primaryBlue} !important;
            }
            
            &.ant-select-item-option-selected {
              background: ${COLORS.$primaryBlue} !important;
              color: white !important;
              font-weight: 600 !important;
              
              &:hover {
                background: ${COLORS.$primaryBlueDark} !important;
                color: white !important;
              }
            }
          }
        }
      }
      
      .ant-pagination-options-quick-jumper {
        display: flex;
        align-items: center;
        height: 32px;
        
        .ant-input {
          border-radius: 16px !important;
          border: 1.5px solid ${COLORS.border} !important;
          height: 32px !important;
          width: 50px !important;
          text-align: center;
          font-weight: 600;
          transition: all 0.3s ease !important;
          
          &:hover {
            border-color: ${COLORS.$primaryBlue} !important;
            box-shadow: 0 2px 8px rgba(24, 144, 255, 0.15) !important;
          }
          
          &:focus {
            border-color: ${COLORS.$primaryBlue} !important;
            box-shadow: 0 0 0 3px rgba(24, 144, 255, 0.15) !important;
          }
        }
      }
    }
    
    .ant-pagination-item {
      border-radius: 50% !important;
      border: 1.5px solid ${COLORS.border};
      min-width: 32px !important;
      height: 32px !important;
      line-height: 28px !important;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.3s ease;
      
      a {
        color: ${COLORS.textDark};
        font-weight: 600;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        height: 100%;
        text-decoration: none;
        font-size: 13px;
      }
      
      &:hover {
        border-color: ${COLORS.$primaryBlue};
        background: rgba(24, 144, 255, 0.1);
        transform: translateY(-1px);
        
        a {
          color: ${COLORS.$primaryBlue};
        }
      }
      
      &.ant-pagination-item-active {
        background: ${COLORS.$primaryBlue};
        border-color: ${COLORS.$primaryBlue};
        transform: translateY(-1px);
        
        a {
          color: white;
          font-weight: 700;
        }
        
        &:hover {
          background: ${COLORS.$primaryBlueDark};
          border-color: ${COLORS.$primaryBlueDark};
          
          a {
            color: white;
          }
        }
      }
    }
    
    .ant-pagination-prev,
    .ant-pagination-next {
      border-radius: 50% !important;
      border: 1.5px solid ${COLORS.border};
      min-width: 32px !important;
      height: 32px !important;
      line-height: 28px !important;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.3s ease;
      
      .anticon {
        color: ${COLORS.textDark};
        font-size: 13px;
      }
      
      &:hover {
        border-color: ${COLORS.$primaryBlue};
        background: rgba(24, 144, 255, 0.1);
        transform: translateY(-1px);
        
        .anticon {
          color: ${COLORS.$primaryBlue};
        }
      }
      
      &.ant-pagination-disabled {
        border-color: #e8e8e8;
        background: #f5f5f5;
        
        .anticon {
          color: ${COLORS.textMuted};
        }
        
        &:hover {
          border-color: #e8e8e8;
          background: #f5f5f5;
          transform: none;
          
          .anticon {
            color: ${COLORS.textMuted};
          }
        }
      }
    }
    
    .ant-pagination-jump-prev,
    .ant-pagination-jump-next {
      border-radius: 50% !important;
      border: 1.5px solid ${COLORS.border};
      min-width: 32px !important;
      height: 32px !important;
      line-height: 28px !important;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.3s ease;
      
      .anticon {
        color: ${COLORS.textMuted};
        font-size: 14px;
      }
      
      &:hover {
        border-color: ${COLORS.$primaryBlue};
        background: rgba(24, 144, 255, 0.1);
        transform: translateY(-1px);
        
        .anticon {
          color: ${COLORS.$primaryBlue};
        }
      }
    }
  }
  
  /* Custom pagination class for better control */
  &.event-management-pagination {
    .ant-pagination {
      justify-content: space-between;
      
      @media (max-width: 768px) {
        flex-direction: row;
        align-items: center;
        justify-content: space-between;
        gap: 8px;
        flex-wrap: nowrap;
        
        /* Hide total text for compact mobile layout */
        .ant-pagination-total-text {
          display: none !important;
        }
        
        .ant-pagination-options {
          margin-left: auto;
          order: 2;
          
          .ant-pagination-options-size-changer {
            .ant-select-selector {
              height: 33px !important;
              min-height: 33px !important;
              font-size: 12px !important;
              padding: 0 8px !important;
            }
          }
        }
        
        /* Create a compact container for pagination buttons */
        .ant-pagination-item,
        .ant-pagination-prev,
        .ant-pagination-next,
        .ant-pagination-jump-prev,
        .ant-pagination-jump-next {
          flex: 0 0 auto !important;
          width: auto !important;
          min-width: 32px !important;
          max-width: 36px !important;
          height: 33px !important;
          margin: 0 1px !important;
          display: inline-flex !important;
          align-items: center !important;
          justify-content: center !important;
          font-size: 13px !important;
          line-height: 31px !important;
          order: 1;
        }
      }
    }
  }

  /* Responsive column adjustments */
  @media (max-width: 1200px) {
    .ant-table-thead > tr > th,
    .ant-table-tbody > tr > td {
      padding: 16px 12px;
      font-size: 13px;
    }
  }

  @media (max-width: 768px) {
    .ant-table {
      font-size: 12px;
    }
    
    .ant-table-thead > tr > th,
    .ant-table-tbody > tr > td {
      padding: 12px 8px;
      font-size: 12px;
      white-space: nowrap;
    }
    
    .ant-table-thead > tr > th {
      height: 48px !important;
      font-size: 11px !important;
      text-transform: uppercase;
      letter-spacing: 0.3px;
    }
  }
`;

// Desktop-only wrapper for the bookings table. Hidden on mobile, where the
// card list takes over.
export const DesktopTableWrapper = styled.div`
  @media (max-width: 767px) {
    display: none;
  }
`;

// Mobile-only stacked card list for bookings (replaces the wide table).
export const MobileBookingCards = styled.div`
  display: none;

  @media (max-width: 767px) {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
`;

export const BookingCard = styled.div`
  background: #fff;
  border: 1px solid #eef0f4;
  border-radius: 14px;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.06);
  padding: 14px 16px;
  cursor: pointer;
  transition: box-shadow 0.15s ease, transform 0.15s ease;

  &:active {
    transform: scale(0.99);
  }

  .card-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 10px;
  }

  .title {
    font-size: 16px;
    font-weight: 700;
    color: ${COLORS.textDark};
    line-height: 1.3;
  }

  .client {
    margin-top: 4px;
    font-size: 13px;
    color: ${COLORS.textLight};
  }

  .unit {
    margin-top: 10px;
  }

  .meta {
    margin-top: 10px;
    display: flex;
    align-items: center;
    gap: 16px;
    font-size: 13px;
    color: ${COLORS.textMedium};

    .meta-item {
      display: inline-flex;
      align-items: center;
      gap: 6px;
    }

    .anticon {
      color: ${COLORS.textLight};
    }
  }
`;

export const BookingCardStatus = styled.span`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  padding: 2px 12px;
  border-radius: 14px;
  font-size: 12px;
  font-weight: 600;
  background: ${props => props.$bg};
  color: ${props => props.$fg};
  border: 1px solid ${props => props.$fg}33;
`;

export const MobileCardsPagination = styled.div`
  display: none;

  @media (max-width: 767px) {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    padding: 16px 0 4px;

    .pg-btn {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 6px;
      height: 44px;
      padding: 0 18px;
      border: 1px solid #e6e8ee;
      border-radius: 12px;
      background: #fff;
      color: ${COLORS.textDark};
      font-size: 14px;
      font-weight: 600;
      cursor: pointer;
      box-shadow: 0 1px 3px rgba(15, 23, 42, 0.06);
      transition: all 0.15s ease;

      &:hover:not(:disabled) {
        border-color: ${COLORS.$primaryBlue};
        color: ${COLORS.$primaryBlue};
      }

      &:active:not(:disabled) {
        transform: scale(0.97);
      }

      &:disabled {
        opacity: 0.45;
        cursor: not-allowed;
        box-shadow: none;
      }
    }

    .pg-info {
      flex: 1;
      text-align: center;
      font-size: 13px;
      font-weight: 600;
      color: ${COLORS.textLight};

      strong {
        color: ${COLORS.textDark};
      }
    }
  }
`;

// Forms Table - matches Patient Management styling
export const FormsTable = styled(Table)`
  /* Animation support */
  animation-fill-mode: both;
  background: white;
  border-radius: 16px;
  border: 1px solid #f0f0f0;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  
  .ant-table {
    border-radius: 0;
    overflow: hidden;
    background: white;
    border: none;
  }
  
  .ant-table-thead > tr > th {
    background: white !important;
    color: ${COLORS.textDark} !important;
    border-bottom: 3px solid ${COLORS.$primaryBlue} !important;
    font-weight: 600 !important;
    font-size: 13px !important;
    height: 52px !important;
    padding: 14px 20px !important;
    text-transform: uppercase !important;
    letter-spacing: 0.5px !important;
    position: relative;
    
    &:first-child {
      border-top-left-radius: 16px;
    }
    
    &:last-child {
      border-top-right-radius: 16px;
    }
  }
  
  .ant-table-tbody > tr {
    transition: all 0.2s ease;
    border-bottom: 1px solid #f5f5f5;
    
    &:hover > td {
      background: #fafbff !important;
      transform: translateY(-1px);
    }
    
    &:last-child {
      border-bottom: none;
      
      td {
        border-bottom: none;
      }
    }
    
    /* Alternating row colors for better readability */
    &:nth-child(even) {
      background: #fafafa;
      
      &:hover > td {
        background: #f0f7ff !important;
      }
    }
  }
  
  .ant-table-tbody > tr > td {
    border-bottom: none;
    padding: 18px 20px;
    font-size: 14px;
    line-height: 1.5;
    vertical-align: middle;
    text-align: left;
    transition: all 0.2s ease;
  }
  
  /* Pagination styling - matching Patient Management with white background */
  .ant-pagination {
    margin: 0 !important;
    padding: 12px 16px !important;
    background: white !important;
    border-top: 1px solid ${COLORS.border};
    border-bottom-left-radius: 16px !important;
    border-bottom-right-radius: 16px !important;
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
    
    .ant-pagination-total-text {
      font-size: 13px;
      color: ${COLORS.textMedium};
      font-weight: 500;
      flex: 1;
    }
    
    .ant-pagination-options {
      display: flex;
      align-items: center;
      gap: 12px;
      height: 32px;
      
      .ant-pagination-options-size-changer {
        height: 32px !important;
        
        .ant-select-selector {
          border-radius: 16px !important;
          border: 1.5px solid ${COLORS.border} !important;
          height: 32px !important;
          min-height: 32px !important;
          min-width: 120px !important;
          transition: all 0.3s ease !important;
          display: flex !important;
          align-items: center !important;
          padding: 0 32px 0 16px !important;
          box-sizing: border-box !important;
          
          &:hover {
            border-color: ${COLORS.$primaryBlue} !important;
            box-shadow: 0 2px 8px rgba(24, 144, 255, 0.15) !important;
          }
        }
        
        &.ant-select-focused .ant-select-selector {
          border-color: ${COLORS.$primaryBlue} !important;
          box-shadow: 0 0 0 3px rgba(24, 144, 255, 0.15) !important;
        }
        
        /* Dropdown menu styling */
        .ant-select-dropdown {
          border-radius: 12px !important;
          box-shadow: 0 6px 24px rgba(0, 0, 0, 0.15) !important;
          border: 1px solid ${COLORS.border} !important;
          
          .ant-select-item {
            padding: 12px 16px !important;
            font-weight: 500 !important;
            transition: all 0.2s ease !important;
            min-height: 32px !important;
            font-size: 14px !important;
            line-height: 1.4 !important;
            display: flex !important;
            align-items: center !important;
            
            &:hover {
              background: rgba(24, 144, 255, 0.1) !important;
              color: ${COLORS.$primaryBlue} !important;
            }
            
            &.ant-select-item-option-selected {
              background: ${COLORS.$primaryBlue} !important;
              color: white !important;
              font-weight: 600 !important;
              
              &:hover {
                background: ${COLORS.$primaryBlueDark} !important;
                color: white !important;
              }
            }
          }
        }
      }
      
      .ant-pagination-options-quick-jumper {
        display: flex;
        align-items: center;
        height: 32px;
        
        .ant-input {
          border-radius: 16px !important;
          border: 1.5px solid ${COLORS.border} !important;
          height: 32px !important;
          width: 50px !important;
          text-align: center;
          font-weight: 600;
          transition: all 0.3s ease !important;
          
          &:hover {
            border-color: ${COLORS.$primaryBlue} !important;
            box-shadow: 0 2px 8px rgba(24, 144, 255, 0.15) !important;
          }
          
          &:focus {
            border-color: ${COLORS.$primaryBlue} !important;
            box-shadow: 0 0 0 3px rgba(24, 144, 255, 0.15) !important;
          }
        }
      }
    }
    
    .ant-pagination-item {
      border-radius: 50% !important;
      border: 1.5px solid ${COLORS.border};
      min-width: 32px !important;
      height: 32px !important;
      line-height: 28px !important;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.3s ease;
      
      a {
        color: ${COLORS.textDark};
        font-weight: 600;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        height: 100%;
        text-decoration: none;
        font-size: 13px;
      }
      
      &:hover {
        border-color: ${COLORS.$primaryBlue};
        background: rgba(24, 144, 255, 0.1);
        transform: translateY(-1px);
        
        a {
          color: ${COLORS.$primaryBlue};
        }
      }
      
      &.ant-pagination-item-active {
        background: ${COLORS.$primaryBlue};
        border-color: ${COLORS.$primaryBlue};
        transform: translateY(-1px);
        
        a {
          color: white;
          font-weight: 700;
        }
        
        &:hover {
          background: ${COLORS.$primaryBlueDark};
          border-color: ${COLORS.$primaryBlueDark};
          
          a {
            color: white;
          }
        }
      }
    }
    
    .ant-pagination-prev,
    .ant-pagination-next {
      border-radius: 50% !important;
      border: 1.5px solid ${COLORS.border};
      min-width: 32px !important;
      height: 32px !important;
      line-height: 28px !important;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.3s ease;
      
      .anticon {
        color: ${COLORS.textDark};
        font-size: 13px;
      }
      
      &:hover {
        border-color: ${COLORS.$primaryBlue};
        background: rgba(24, 144, 255, 0.1);
        transform: translateY(-1px);
        
        .anticon {
          color: ${COLORS.$primaryBlue};
        }
      }
      
      &.ant-pagination-disabled {
        border-color: #e8e8e8;
        background: #f5f5f5;
        
        .anticon {
          color: ${COLORS.textMuted};
        }
        
        &:hover {
          border-color: #e8e8e8;
          background: #f5f5f5;
          transform: none;
          
          .anticon {
            color: ${COLORS.textMuted};
          }
        }
      }
    }
    
    .ant-pagination-jump-prev,
    .ant-pagination-jump-next {
      border-radius: 50% !important;
      border: 1.5px solid ${COLORS.border};
      min-width: 32px !important;
      height: 32px !important;
      line-height: 28px !important;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.3s ease;
      
      .anticon {
        color: ${COLORS.textMuted};
        font-size: 14px;
      }
      
      &:hover {
        border-color: ${COLORS.$primaryBlue};
        background: rgba(24, 144, 255, 0.1);
        transform: translateY(-1px);
        
        .anticon {
          color: ${COLORS.$primaryBlue};
        }
      }
    }
  }
  
  /* Responsive column adjustments */
  @media (max-width: 1200px) {
    .ant-table-thead > tr > th,
    .ant-table-tbody > tr > td {
      padding: 16px 12px;
      font-size: 13px;
    }
  }

  @media (max-width: 768px) {
    .ant-table {
      font-size: 12px;
    }
    
    .ant-table-thead > tr > th,
    .ant-table-tbody > tr > td {
      padding: 12px 8px;
      font-size: 12px;
      white-space: nowrap;
    }
    
    .ant-table-thead > tr > th {
      height: 48px !important;
      font-size: 11px !important;
      text-transform: uppercase;
      letter-spacing: 0.3px;
    }
  }
`;

// Legacy styles for backward compatibility (keeping the old EventManagement working)
export const FiltersContainer = styled.div`
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  padding: 32px 48px;
  margin: 0;
  width: 100%;
  box-sizing: border-box;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  position: sticky;
  top: 0;
  z-index: 10;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

  @media (max-width: 768px) {
    padding: 24px 40px; 
    position: relative;
  }
`;

export const StyledTable = styled(Table)`
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);

  .ant-table {
    background: transparent;
  }

  .ant-table-container {
    border-radius: 0;
    overflow: hidden;
    box-shadow: none;
    background: white;
  }

  .ant-table-thead > tr > th {
    background: linear-gradient(135deg, ${COLORS.primary} 0%, #0056b3 100%);
    color: white;
    font-weight: 700;
    font-size: 14px;
    border: none;
    padding: 20px 16px;
    text-align: center;
    position: relative;

    &::before {
      content: '';
      position: absolute;
      top: 0;
      right: 0;
      width: 1px;
      height: 100%;
      background: rgba(255, 255, 255, 0.2);
    }

    &:last-child::before {
      display: none;
    }
  }

  .ant-table-tbody > tr {
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

    &:hover {
      background: linear-gradient(135deg, #f8f9ff 0%, #e8f0ff 100%);
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    }

    > td {
      border-bottom: 1px solid #f0f0f0;
      padding: 16px;
      text-align: center;
      vertical-align: middle;
    }
  }

  .ant-table-tbody > tr:last-child > td {
    border-bottom: none;
  }

  .ant-empty {
    padding: 40px 20px;
  }
`;

export const StyledTableContainer = styled.div`
  background: transparent;
  padding: 32px 48px 48px 48px;
  margin: 0;
  width: 100%;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 32px 40px 48px 40px; 
  }
`;

export const EventCountTag = styled.span`
  background: linear-gradient(135deg, ${COLORS.success} 0%, #28a745 100%);
  color: white;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.5px;
  display: inline-block;
  margin-left: 12px;
  box-shadow: 0 2px 8px rgba(40, 167, 69, 0.3);
  text-transform: uppercase;
`;

export const PaginationText = styled.span`
  color: #666;
  font-weight: 500;
  font-size: 13px;
  letter-spacing: 0.3px;
`;

export const StyledInput = styled(Input)`
  border-radius: 10px;
  border: 1.5px solid #e9ecef;
  padding: 12px 16px;
  font-size: 14px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  background: rgba(255, 255, 255, 0.9);

  &:hover {
    border-color: ${COLORS.primary};
    background: white;
  }

  &:focus {
    border-color: ${COLORS.primary};
    box-shadow: 0 0 0 3px rgba(24, 144, 255, 0.1);
    background: white;
  }

  &::placeholder {
    color: #adb5bd;
    font-weight: 400;
  }
`;

export const StyledSelect = styled(Select)`
  .ant-select-selector {
    border-radius: 10px !important;
    border: 1px solid #e9ecef !important;
    padding: 8px 12px !important;
    background: rgba(255, 255, 255, 0.9) !important;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
    height: auto !important;
    min-height: 40px !important;
  }

  &:hover .ant-select-selector {
    border-color: ${COLORS.primary} !important;
    background: white !important;
  }

  &.ant-select-focused .ant-select-selector {
    border-color: ${COLORS.primary} !important;
    box-shadow: 0 0 0 3px rgba(24, 144, 255, 0.1) !important;
    background: white !important;
  }

  .ant-select-selection-placeholder {
    color: #adb5bd !important;
    font-weight: 400 !important;
  }
`;

export const StyledRangePicker = styled(DatePicker.RangePicker)`
  .ant-picker-input > input {
    border-radius: 10px;
    font-size: 14px;
  }

  .ant-picker {
    border-radius: 10px;
    border: 1.5px solid #e9ecef;
    padding: 12px 16px;
    background: rgba(255, 255, 255, 0.9);
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    width: 100%;
  }

  .ant-picker:hover {
    border-color: ${COLORS.primary};
    background: white;
  }

  .ant-picker.ant-picker-focused {
    border-color: ${COLORS.primary};
    box-shadow: 0 0 0 3px rgba(24, 144, 255, 0.1);
    background: white;
  }
`;

export const MobileFilterDrawer = styled.div`
  .ant-drawer-content-wrapper {
    width: 300px !important;
  }

  .ant-drawer-header {
    background: linear-gradient(135deg, ${COLORS.primary} 0%, #0056b3 100%);
    border: none;
  }

  .ant-drawer-header .ant-drawer-title {
    color: white;
    font-weight: 700;
  }

  .ant-drawer-close {
    color: white;
  }
`;

// --- Mobile bookings header (search + filter icon + New) ---------------------
export const MobileFilterBar = styled.div`
  display: none;
  @media (max-width: 767px) {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 10px;

    .search {
      flex: 1 1 auto;
      min-width: 0;
      .ant-input-affix-wrapper { height: 44px !important; border-radius: 10px; }
    }

    .icon-btn {
      flex: 0 0 auto;
      width: 44px;
      height: 44px;
      border: 1px solid ${COLORS.border};
      border-radius: 10px;
      background: #fff;
      display: flex;
      align-items: center;
      justify-content: center;
      position: relative;
      color: ${COLORS.textMedium};
      cursor: pointer;

      &.active {
        color: ${COLORS.$primaryBlue};
        border-color: ${COLORS.$primaryBlue};
        background: ${COLORS.$primaryBlue}0F;
      }

      .badge {
        position: absolute;
        top: -6px;
        right: -6px;
        min-width: 18px;
        height: 18px;
        padding: 0 4px;
        background: ${COLORS.$primaryBlue};
        color: #fff;
        border-radius: 9px;
        font-size: 11px;
        font-weight: 600;
        display: flex;
        align-items: center;
        justify-content: center;
        border: 2px solid #fff;
      }
    }

    .new-btn {
      flex: 0 0 auto;
      height: 44px;
      border-radius: 10px;
      font-weight: 600;
    }
  }
`;

export const DesktopFilters = styled.div`
  @media (max-width: 767px) {
    display: none;
  }
`;

export const StatusChipsRow = styled.div`
  display: none;
  @media (max-width: 767px) {
    display: flex;
    gap: 8px;
    overflow-x: auto;
    padding-bottom: 4px;
    margin-bottom: 12px;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
    &::-webkit-scrollbar { display: none; }
  }
`;

// Reusable pill used for status filters and the filter-sheet location chips.
export const FilterChip = styled.button`
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 34px;
  padding: 0 14px;
  border-radius: 18px;
  border: 1px solid ${props => (props.$active ? COLORS.$primaryBlue : COLORS.border)};
  background: ${props => (props.$active ? `${COLORS.$primaryBlue}0F` : '#fff')};
  color: ${props => (props.$active ? COLORS.$primaryBlue : COLORS.textDark)};
  font-size: 14px;
  font-weight: 500;
  white-space: nowrap;
  cursor: pointer;
  transition: all 0.2s ease;

  .dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    flex: 0 0 auto;
  }

  .count {
    font-weight: 700;
    color: ${props => (props.$active ? COLORS.$primaryBlue : props.$countColor || COLORS.textMedium)};
  }
`;

// --- Bottom-sheet filter body (mobile) ---------------------------------------
export const FilterSheetSection = styled.div`
  margin-bottom: 20px;

  .sheet-label {
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: ${COLORS.textMedium};
    margin-bottom: 10px;
  }

  .chip-wrap {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }
`;

// Modal Styling
export const StyledModal = styled(Modal)`
  .ant-modal-content {
    border-radius: 12px;
  }
  
  .ant-modal-header {
    border-bottom: 1px solid #f0f0f0;
    padding: 24px 24px 16px;
    
    .ant-modal-title {
      font-size: 18px;
      font-weight: 600;
      color: ${COLORS.textDark};
    }
  }
  
  .ant-modal-body {
    padding: 24px;
  }
  
  .ant-modal-footer {
    border-top: 1px solid #f0f0f0;
    padding: 16px 24px 24px;
    text-align: right;
    
    .ant-btn {
      margin-left: 8px;
      border-radius: 6px;
      
      &.ant-btn-primary {
        background: ${COLORS.$primaryBlue};
        border-color: ${COLORS.$primaryBlue};
        
        &:hover {
          background: ${COLORS.$primaryBlueHover};
          border-color: ${COLORS.$primaryBlueHover};
        }
      }
    }
  }
`;

export const FormSection = styled.div`
  .ant-form-item {
    margin-bottom: 20px;
    
    .ant-form-item-label {
      label {
        font-weight: 500;
        color: ${COLORS.textDark};
        font-size: 14px;
      }
    }
  }
  
  .ant-input,
  .ant-input-number,
  .ant-select-selector {
    border-radius: 6px;
    border: 1px solid #d9d9d9;
    height: 40px;
    display: flex;
    align-items: center;
    font-size: 14px;
    padding: 0 16px;
    
    &:hover {
      border-color: ${COLORS.$primaryBlue};
    }
    
    &:focus,
    &.ant-input-focused,
    &.ant-select-focused .ant-select-selector {
      border-color: ${COLORS.$primaryBlue};
      box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
    }
  }
  
  .ant-input-number {
    width: 100%;
    
    .ant-input-number-input {
      height: 36px;
      line-height: 28px;
      padding: 0 16px;
      font-size: 14px;
    }
  }
  
  .ant-input-group-addon {
    height: 40px;
    display: flex;
    align-items: center;
  }
  
  .ant-color-picker-trigger {
    height: 40px;
    border-radius: 6px;
    display: flex;
    align-items: center;
    padding: 8px 16px;
  }
  
  /* Better text area styling */
  .ant-input {
    &[type="textarea"] {
      height: auto;
      padding: 12px 16px;
      line-height: 1.6;
    }
  }
  
  /* Blue checkboxes with larger size */
  .ant-checkbox-wrapper {
    align-items: center;
    
    .ant-checkbox {
      .ant-checkbox-inner {
        width: 18px;
        height: 18px;
        border: 1.5px solid #d9d9d9;
        border-radius: 4px;
        background-color: #fff;
        transition: all 0.3s ease;
        
        &:after {
          width: 6.71px;
          height: 10.97px;
          border: 1.5px solid #fff;
          border-top: 0;
          border-left: 0;
          transform: rotate(45deg) scale(1) translate(-50%, -50%);
          opacity: 0;
          transition: all 0.2s cubic-bezier(0.12, 0.4, 0.29, 1.46) 0.1s;
          position: absolute;
          top: 50%;
          left: 21.5%;
        }
      }
      
      &:hover .ant-checkbox-inner {
        border-color: ${COLORS.$primaryBlue};
      }
      
      &.ant-checkbox-checked {
        .ant-checkbox-inner {
          background-color: ${COLORS.$primaryBlue};
          border-color: ${COLORS.$primaryBlue};
          
          &:after {
            opacity: 1;
            transform: rotate(45deg) scale(1) translate(-50%, -50%);
          }
        }
        
        &:hover .ant-checkbox-inner {
          background-color: ${COLORS.$primaryBlueHover};
          border-color: ${COLORS.$primaryBlueHover};
        }
      }
      
      &.ant-checkbox-indeterminate .ant-checkbox-inner {
        background-color: ${COLORS.$primaryBlue};
        border-color: ${COLORS.$primaryBlue};
        
        &:after {
          width: 8px;
          height: 1px;
          background-color: #fff;
          border: 0;
          transform: translate(-50%, -50%);
          opacity: 1;
        }
      }
    }
    
    span {
      font-size: 14px;
      color: ${COLORS.textDark};
      font-weight: 500;
    }
  }
`;

// Modern Calendar View Styles
export const CalendarContainer = styled.div`
  .time-column {
    width: 80px;
    flex-shrink: 0;
  }
`;

export const CalendarHeader = styled.div`
  display: flex;
  background: #fafafa;
  border-bottom: 2px solid #f0f0f0;
  font-weight: 600;
  
  .time-column,
  .day-header {
    padding: 16px 8px;
    text-align: center;
    flex: 1;
    border-right: 1px solid #f0f0f0;
    
    &:last-child {
      border-right: none;
    }
  }
  
  .day-header {
    .day-name {
      font-size: 12px;
      color: #666;
      text-transform: uppercase;
      margin-bottom: 4px;
    }
    
    .day-number {
      font-size: 14px;
      color: ${COLORS.textDark};
    }
  }
`;

export const CalendarGrid = styled.div`
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  overflow: hidden;
  
  ${props => props.viewMode === 'week' && `
    .calendar-body {
      .time-row {
        display: flex;
        min-height: 60px;
        border-bottom: 1px solid #f0f0f0;
        
        &:last-child {
          border-bottom: none;
        }
        
        .time-label {
          width: 80px;
          flex-shrink: 0;
          padding: 8px;
          font-size: 12px;
          color: #666;
          text-align: center;
          background: #fafafa;
          border-right: 1px solid #f0f0f0;
          display: flex;
          align-items: flex-start;
          justify-content: center;
        }
        
        .time-slot {
          flex: 1;
          border-right: 1px solid #f0f0f0;
          position: relative;
          min-height: 60px;
          cursor: pointer;
          transition: background-color 0.2s;
          
          &:hover {
            background-color: #f8f9fa;
          }
          
          &:last-child {
            border-right: none;
          }
        }
      }
    }
  `}
  
  ${props => props.viewMode === 'month' && `
    .calendar-body {
      .calendar-week {
        display: flex;
        
        .month-day {
          flex: 1;
          min-height: 120px;
          border-right: 1px solid #f0f0f0;
          border-bottom: 1px solid #f0f0f0;
          padding: 8px;
          cursor: pointer;
          position: relative;
          
          &:last-child {
            border-right: none;
          }
          
          &.other-month {
            background: #fafafa;
            color: #ccc;
          }
          
          &.today {
            background: #e6f7ff;
            
            .day-number {
              background: ${COLORS.$primaryBlue};
              color: white;
              border-radius: 50%;
              width: 24px;
              height: 24px;
              display: flex;
              align-items: center;
              justify-content: center;
              font-weight: bold;
            }
          }
          
          &:hover:not(.other-month) {
            background: #f0f8ff;
          }
        }
      }
    }
  `}
`;

export const CalendarDay = styled.div`
  .day-number {
    font-weight: 600;
    margin-bottom: 4px;
    font-size: 14px;
  }
  
  .day-events {
    display: flex;
    flex-wrap: wrap;
    gap: 2px;
    
    .event-dot {
      width: 8px;
      height: 8px;
      border-radius: 50%;
      cursor: pointer;
    }
    
    .blocked-indicator {
      color: #ff4d4f;
      font-size: 10px;
    }
    
    .more-events {
      font-size: 10px;
      color: #666;
      background: #f0f0f0;
      padding: 2px 4px;
      border-radius: 2px;
    }
  }
`;

export const TimeSlotEvent = styled.div`
  background: ${props => props.color || COLORS.$primaryBlue};
  color: white;
  padding: 4px 8px;
  margin: 2px;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
  opacity: ${props => props.status === 'pending' ? 0.7 : 1};
  border-left: 4px solid ${props => props.status === 'confirmed' ? '#52c41a' : '#faad14'};
  
  .event-name {
    font-weight: 500;
    margin-bottom: 2px;
  }
  
  .event-time {
    font-size: 10px;
    opacity: 0.9;
  }
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }
`;

export const BlockedTimeSlot = styled.div`
  background: #ff4d4f;
  color: white;
  padding: 4px 8px;
  margin: 2px;
  border-radius: 4px;
  font-size: 12px;
  display: flex;
  align-items: center;
  gap: 4px;
  opacity: 0.8;
`;

export const CalendarControls = styled.div`
  display: flex;
  align-items: center;
  
  .ant-btn-group {
    margin-right: 16px;
    
    .ant-btn {
      border-radius: 6px;
      
      &:first-child {
        border-top-right-radius: 0;
        border-bottom-right-radius: 0;
      }
      
      &:last-child {
        border-top-left-radius: 0;
        border-bottom-left-radius: 0;
      }
      
      &:not(:first-child):not(:last-child) {
        border-radius: 0;
      }
    }
  }
`;

export const ViewToggle = styled.div`
  .ant-btn-group {
    .ant-btn {
      border-radius: 6px;
      
      &:first-child {
        border-top-right-radius: 0;
        border-bottom-right-radius: 0;
      }
      
      &:last-child {
        border-top-left-radius: 0;
        border-bottom-left-radius: 0;
      }
    }
  }
`;

export const LegendContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
`;

export const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  
  .legend-color {
    width: 12px;
    height: 12px;
    border-radius: 2px;
    
    &.blocked {
      background: #ff4d4f;
    }
  }
`;

export const QuickActionsPanel = styled.div`
  background: #fafafa;
  padding: 16px;
  border-radius: 6px;
  margin-bottom: 16px;
`;

export const CheckboxGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 8px;
  margin-top: 8px;
  width: 100%; /* Ensure grid takes full width */
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(4, 1fr);
    gap: 6px;
  }
  
  @media (max-width: 480px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 8px;
  }
  
  .checkbox-item {
    position: relative;
    width: 100%; /* Ensure full width */
    
    .ant-checkbox-wrapper {
      display: none; /* Hide the default checkbox */
    }
    
    .day-button {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 12px 8px;
      background-color: #ffffff;
      border: 1.5px solid #e8e8e8;
      border-radius: 12px;
      cursor: pointer;
      transition: all 0.2s ease;
      min-height: 64px;
      text-align: center;
      user-select: none;
      width: 100%; /* Full width of grid cell */
      box-sizing: border-box;
      
      @media (max-width: 480px) {
        padding: 16px 12px; /* More padding on mobile for better touch */
        min-height: 72px; /* Taller on mobile */
      }
      
      &:hover {
        border-color: ${COLORS.$primaryBlue};
        background-color: rgba(24, 144, 255, 0.05);
        transform: translateY(-1px);
        box-shadow: 0 4px 12px rgba(24, 144, 255, 0.15);
      }
      
      .day-abbr {
        font-size: 12px;
        font-weight: 600;
        color: ${COLORS.textMedium};
        margin-bottom: 4px;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        
        @media (max-width: 480px) {
          font-size: 13px; /* Slightly larger on mobile */
        }
      }
      
      .day-full {
        font-size: 14px;
        font-weight: 500;
        color: ${COLORS.textDark};
        line-height: 1.2;
        
        @media (max-width: 480px) {
          font-size: 15px; /* Slightly larger on mobile */
          font-weight: 600;
        }
      }
      
      &.selected {
        background-color: ${COLORS.$primaryBlue};
        border-color: ${COLORS.$primaryBlue};
        color: white;
        box-shadow: 0 4px 12px rgba(24, 144, 255, 0.3);
        
        .day-abbr,
        .day-full {
          color: white;
        }
        
        &:hover {
          background-color: #0c7cd5;
          border-color: #0c7cd5;
          transform: translateY(-2px);
          box-shadow: 0 6px 16px rgba(24, 144, 255, 0.4);
        }
      }
      
      &.weekend {
        border-color: #ffa940;
        
        &:hover {
          border-color: #fa8c16;
          background-color: rgba(255, 169, 64, 0.05);
        }
        
        &.selected {
          background-color: #ffa940;
          border-color: #ffa940;
          
          &:hover {
            background-color: #fa8c16;
            border-color: #fa8c16;
          }
        }
      }
    }
  }
`;

export const StyledTabs = styled(Tabs)`
  margin: 0 !important;
  background: transparent;
  padding-top: 16px;
  padding-bottom: 8px;
  margin-bottom: 0;
  
  .ant-tabs-nav {
    margin: 0 !important;
    margin-bottom: 0 !important;
    
    &::before {
      display: none !important;
    }
    
    /* Add border line after the tab navigation */
    &::after {
      content: '';
      position: absolute;
      bottom: -8px;
      left: 0;
      right: 0;
      height: 1px;
      background: ${COLORS.border};
    }
  }
  
  /* Hide ink bar */
  .ant-tabs-ink-bar {
    display: none !important;
  }
  
  /* Make the tab strip horizontally scrollable on small widths */
  .ant-tabs-nav-wrap {
    overflow-x: auto;
    scrollbar-width: none; /* Firefox */
    
    &::-webkit-scrollbar {
      display: none; /* WebKit */
    }
  }
  
  .ant-tabs-nav-list {
    display: flex;
    gap: 4px;
    align-items: center;
    justify-content: flex-start;
    width: auto;
    
    .ant-tabs-tab {
      position: relative;
      padding: 0;
      margin: 0;
      background: transparent;
      border: none;
      transition: all 0.2s ease;
      flex: none;
      width: auto;
      
      /* Remove dividers */
      &::before {
        display: none;
      }
      
      .ant-tabs-tab-btn {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        padding: 10px 14px;
        border-radius: 999px;
        color: ${COLORS.textMedium};
        font-weight: 500;
        font-size: 14px;
        transition: background-color .2s ease, color .2s ease;
        background: transparent;
        border: none;
        min-height: 40px;
        white-space: nowrap;
        
        .anticon {
          font-size: 14px;
        }
        
        /* Default badge styling - gray */
        .tab-badge {
          background-color: #8c8c8c !important;
          transition: background-color .2s ease;
        }
      }
      
      &:hover .ant-tabs-tab-btn {
        color: ${COLORS.textDark};
        background: rgba(24, 144, 255, 0.08);
      }
      
      &.ant-tabs-tab-active .ant-tabs-tab-btn {
        color: ${COLORS.$primaryBlue};
        background: #eef4ff;
        font-weight: 600;
        box-shadow: 0 1px 3px rgba(0,0,0,.06);
        border: 1px solid rgba(24, 144, 255, 0.3);
        
        /* Active badge styling - show original color */
        .tab-badge {
          background-color: var(--badge-color) !important;
        }
      }
      
      /* Visible keyboard focus */
      .ant-tabs-tab-btn:focus-visible {
        outline: 2px solid ${COLORS.$primaryBlue};
        outline-offset: 2px;
        border-radius: 999px;
      }
    }
  }
  
  /* Add spacing for tab content */
  .ant-tabs-content-holder {
    padding-top: 20px;
  }
  
  /* Responsive adjustments */
  @media (max-width: 768px) {
    .ant-tabs-nav-list {
      .ant-tabs-tab {
        .ant-tabs-tab-btn {
          padding: 8px 12px;
          font-size: 13px;
          min-height: 36px;
        }
      }
    }
  }
`;

export const TabBadge = styled.span`
  --badge-color: ${props => props.color || '#1890ff'};
  background-color: #8c8c8c;
  border-radius: 10px;
  font-size: 12px;
  min-width: 20px;
  height: 20px;
  line-height: 20px;
  padding: 0 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
  transition: background-color .2s ease;
`;
